import { ReactElement, useState } from 'react'
import { Box, TextField, Typography } from '@mui/material'

interface Props {
  initialValue: number
  onCommit: (value: number) => void
}

/**
 * Small numeric input used inside the scene context menu to edit the
 * bracket distance (Klammerbreite) of a clip plane in bracket mode.
 *
 * Manages its own local input state so it works even though the context menu
 * does not re-render on every keystroke. Valid values are committed live on
 * every change; invalid/empty input is kept in the field while typing and
 * reverted to the last valid value on blur.
 */
const BracketDistanceField = ({ initialValue, onCommit }: Props): ReactElement => {
  const [value, setValue] = useState(String(initialValue))

  const parse = (raw: string): number | null => {
    const parsed = Number(raw.replace(',', '.'))

    if (!Number.isFinite(parsed) || parsed <= 0) return null

    return parsed
  }

  const handleChange = (raw: string) => {
    setValue(raw)

    const parsed = parse(raw)
    if (parsed !== null) onCommit(parsed)
  }

  const handleBlur = () => {
    if (parse(value) === null) setValue(String(initialValue))
  }

  return (
    <Box p={2} sx={{ minWidth: 200, cursor: 'default' }}>
      <Typography fontSize={13} mb={0.5}>
        Klammerbreite (m)
      </Typography>
      <TextField
        size="small"
        type="number"
        value={value}
        autoFocus
        fullWidth
        inputProps={{ step: 0.1, min: 0 }}
        onChange={event => handleChange(event.target.value)}
        onKeyDown={event => {
          // Keep typing from triggering scene keyboard shortcuts.
          event.stopPropagation()
        }}
        onBlur={handleBlur}
      />
    </Box>
  )
}

export default BracketDistanceField
